import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Star, Loader2, Camera, User, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReviewSubmitModal({ isOpen, onClose, authHeader }) {
  const [formData, setFormData] = useState({
    reviewer_name: "",
    review_text: "",
    rating: 5,
    image_url: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reviewer_name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    
    if (!formData.review_text.trim()) {
      toast.error("Please write your review");
      return;
    }
    
    if (formData.review_text.length < 20) {
      toast.error("Please write at least 20 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/reviews/submit`, formData, {
        headers: authHeader
      });
      toast.success("Thank you! Your review will be published after approval.");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Share Your Experience</h3>
                <p className="text-sm text-gray-500">Help other Bahamians make informed decisions</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              data-testid="close-review-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Star Rating */}
            <div>
              <Label className="mb-2 block">Your Rating</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-8 h-8 ${
                        star <= (hoverRating || formData.rating)
                          ? "text-amber-400 fill-amber-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="reviewer_name">Your Name (as displayed)</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="reviewer_name"
                  value={formData.reviewer_name}
                  onChange={(e) => setFormData({ ...formData, reviewer_name: e.target.value })}
                  className="pl-10"
                  placeholder="e.g., Marcus T. or Marcus from Nassau"
                  data-testid="reviewer-name-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Tip: Use first name and last initial for privacy</p>
            </div>

            {/* Review Text */}
            <div>
              <Label htmlFor="review_text">Your Review</Label>
              <div className="relative mt-1">
                <Textarea
                  id="review_text"
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  className="min-h-[120px]"
                  placeholder="What was your experience with Financify? Did it help you understand your loan options?"
                  data-testid="review-text-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{formData.review_text.length}/500 characters</p>
            </div>

            {/* Optional Photo */}
            <div>
              <Label htmlFor="image_url" className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photo URL (Optional)
              </Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="mt-1"
                placeholder="https://example.com/your-photo.jpg"
                data-testid="review-image-input"
              />
              <p className="text-xs text-gray-500 mt-1">Add a profile photo to build trust (optional)</p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Your review will be visible on our homepage after approval. 
                We may edit for length but will never change your message.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#52BFB8] hover:bg-[#3DA8A1] text-white"
                disabled={isSubmitting}
                data-testid="submit-review-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Review"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
